// quantity-bundle.js — custom element <quantity-bundle>
//
// Precisa ser custom element (e nao init por DOMContentLoaded): quando o cliente
// troca de variante com swap de produto, o Dawn substitui o <product-info>
// inteiro via HTMLUpdateUtility.setInnerHTML, o que destrói o DOM do bundle e
// todos os listeners. O connectedCallback re-inicializa sozinho no DOM novo.

if (!customElements.get("quantity-bundle")) {
  customElements.define(
    "quantity-bundle",
    class QuantityBundle extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.cacheElements();
        if (!this.buyButton || !this.currentPriceEl || !this.pixPriceEl) return;

        this.initialized = true;
        this.readData();
        this.bindEvents();
        this.updatePrices();
        this.updateVisualSelection();
        this.updateButtonState();
        if (this.mode === "quantity") this.setQuantity(this.selectedQuantity);
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onChange);
        this.buyButton?.removeEventListener("click", this.onSubmit);
        if (this.onStep) this.removeEventListener("click", this.onStep);
        if (this.onQtyInput)
          this.qtyInput?.removeEventListener("change", this.onQtyInput);
        this.unsubscribeVariant?.();
        this.initialized = false;
      }

      cacheElements() {
        this.buyButton = this.querySelector(".quantity-bundle__buy-button");
        this.unitPriceEl = this.querySelector(".quantity-bundle__price-unit");
        this.comparePriceEl = this.querySelector(
          ".quantity-bundle__compare-price",
        );
        this.currentPriceEl = this.querySelector(
          ".quantity-bundle__current-price",
        );
        this.pixPriceEl = this.querySelector(".quantity-bundle__pix-price");
        this.parcelsPriceEl = this.querySelector(
          ".quantity-bundle__price-parcels",
        );
        this.tagDiscountEl = this.querySelector(
          ".quantity-bundle__discount-tag",
        );

        // Modo combo (seletor de quantidade)
        this.shippingNoteEl = this.querySelector("[data-shipping-note]");
        this.shippingBadgeEl = this.shippingNoteEl
          ? this.shippingNoteEl.closest(".quantity-bundle__trust-badge")
          : null;
        this.shippingNoteTemplate = this.shippingNoteEl
          ? this.shippingNoteEl.textContent.trim()
          : "";

        this.qtyInput = this.querySelector(".quantity-bundle__qty-input");
        this.qtyTotalEl = this.querySelector(".quantity-bundle__qty-total-value");
        this.stepperBtns = [
          ...this.querySelectorAll(".quantity-bundle__stepper-btn"),
        ];
      }

      readData() {
        this.productId = this.dataset.productId;
        this.variantId = this.dataset.variantId;
        this.variantAvailable = this.dataset.variantAvailable === "true";
        this.productHandle = this.dataset.productHandle;

        // Section do product-info que envolve o bundle (filtra o evento de variante)
        this.productInfo = this.closest("product-info");

        this.parcelas = parseFloat(this.parcelsPriceEl.dataset.parcelas || 1);

        // Descontos por posição, vindos do Liquid como "15/20/25"
        this.discounts = (this.dataset.discounts || "0")
          .split("/")
          .map((value) => parseFloat(value));

        // Unidades por embalagem: divisor do preco por unidade (Liquid resolve
        // metafield do produto > campo do editor > 1).
        this.unitsPerPack = parseInt(this.dataset.unitsPerPack) || 1;

        // Minimo para frete gratis, em centavos (0 = recurso desligado)
        this.freeShippingMin = parseInt(this.dataset.freeShippingThreshold) || 0;

        // "tiers" (faixas de desconto) ou "quantity" (combo com seletor)
        this.mode = this.dataset.mode === "quantity" ? "quantity" : "tiers";
        this.qtyMin = parseInt(this.qtyInput?.min) || 1;
        this.qtyMax = parseInt(this.qtyInput?.max) || 99;

        this.quantities = [
          ...this.querySelectorAll(".quantity-bundle__option"),
        ].map((option) => parseInt(option.dataset.quantity));

        // Preço da variante atual (centavos → reais)
        this.basePrice =
          parseFloat(this.currentPriceEl.dataset.currentPrice) / 100;

        this.pixDiscount = parseFloat(this.pixPriceEl.dataset.pixDiscount || 0);

        // Sufixo do texto do Pix (ex: "no Pix"), preservado nas atualizações
        this.pixSuffix = this.pixPriceEl.textContent
          .trim()
          .split(/\s+/)
          .slice(2)
          .join(" ");

        this.originalButtonText = this.buyButton.textContent.trim();
        this.isSubmitting = false;

        const { value, position } = this.getSelectedQuantityAndPosition();
        this.selectedQuantity = value;
        this.position = position;
      }

      bindEvents() {
        // Delegação: sobrevive a qualquer re-render interno das opções.
        // O <label for> já marca o radio e dispara o change nativamente.
        this.onChange = (event) => {
          if (!event.target.matches(".quantity-bundle__radio-input")) return;
          this.selectedQuantity = parseInt(event.target.value);
          this.position = parseInt(event.target.dataset.position);
          this.updatePrices();
          this.updateVisualSelection();
        };
        this.addEventListener("change", this.onChange);

        this.onSubmit = (event) => {
          event.preventDefault();
          this.addToCart();
        };
        this.buyButton.addEventListener("click", this.onSubmit);

        if (this.mode === "quantity") {
          this.onStep = (event) => {
            const btn = event.target.closest(".quantity-bundle__stepper-btn");
            if (!btn) return;
            event.preventDefault();
            const step = parseInt(btn.dataset.qtyStep) || 0;
            this.setQuantity(this.selectedQuantity + step);
          };
          this.addEventListener("click", this.onStep);

          this.onQtyInput = () => this.setQuantity(this.qtyInput.value);
          this.qtyInput?.addEventListener("change", this.onQtyInput);
        }

        // Troca de variante sem swap de produto: o DOM permanece, só os dados mudam
        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          this.unsubscribeVariant = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this),
          );
        }
      }

      onVariantChange(event) {
        if (this.productInfo) {
          const eventSectionId = String(event?.data?.sectionId || "");
          const ownSectionId = String(
            this.productInfo.dataset.originalSection ||
              this.productInfo.dataset.section ||
              "",
          );
          if (eventSectionId && ownSectionId && eventSectionId !== ownSectionId) {
            return;
          }
        }

        const variant = event?.data?.variant;

        if (!variant) {
          this.variantAvailable = false;
          this.updateButtonState();
          return;
        }

        this.variantId = String(variant.id);
        this.variantAvailable = variant.available !== false;
        this.basePrice = parseFloat(variant.price) / 100;

        this.updatePrices();
        this.updateButtonState();
      }

      clampQty(value) {
        const n = parseInt(value);
        if (isNaN(n)) return this.qtyMin || 1;
        return Math.min(Math.max(n, this.qtyMin || 1), this.qtyMax || 99);
      }

      setQuantity(value) {
        const qty = this.clampQty(value);
        this.selectedQuantity = qty;
        if (this.qtyInput) this.qtyInput.value = qty;
        this.stepperBtns.forEach((btn) => {
          const step = parseInt(btn.dataset.qtyStep) || 0;
          btn.disabled =
            (step < 0 && qty <= (this.qtyMin || 1)) ||
            (step > 0 && qty >= (this.qtyMax || 99));
        });
        this.updatePrices();
      }

      getSelectedQuantityAndPosition() {
        if (this.mode === "quantity") {
          return { value: this.clampQty(this.qtyInput?.value), position: 1 };
        }

        const checked =
          this.querySelector(".quantity-bundle__radio-input:checked") ||
          this.querySelector(".quantity-bundle__radio-input");

        if (!checked) return { value: this.quantities[0] || 1, position: 1 };

        return {
          value: parseInt(checked.value),
          position: parseInt(checked.dataset.position),
        };
      }

      updatePrices() {
        const quantity = this.selectedQuantity;
        const discountPercentRaw = this.discounts[this.position - 1] || 0;
        const discountPercent = Math.round(discountPercentRaw);
        const discountDecimal = (100 - discountPercentRaw) / 100;

        const totalCurrent = this.basePrice * quantity * discountDecimal;
        const totalCompare = this.basePrice * quantity;
        const priceUnit = totalCurrent / quantity;
        const pixPrice = totalCurrent * ((100 - this.pixDiscount) / 100);
        const parcelasValue = totalCurrent / this.parcelas;

        this.unitPriceEl.textContent = `${this.formatPrice(priceUnit)} cada`;
        this.currentPriceEl.textContent = this.formatPrice(totalCurrent);
        this.parcelsPriceEl.textContent = `ou ${
          this.parcelas
        }x de ${this.formatPrice(parcelasValue)}`;
        this.pixPriceEl.textContent = `${this.formatPrice(pixPrice)} ${
          this.pixSuffix
        }`.trim();

        if (this.qtyTotalEl) {
          this.qtyTotalEl.textContent = this.formatPrice(totalCurrent);
        }

        this.updateShippingNote(totalCurrent, quantity);

        this.tagDiscountEl.textContent = `${discountPercent}% OFF`;

        if (discountPercent <= 0) {
          this.comparePriceEl.style.display = "none";
          this.tagDiscountEl.style.display = "none";
        } else {
          this.comparePriceEl.style.display = "";
          this.tagDiscountEl.style.display = "";
          this.comparePriceEl.textContent = this.formatPrice(totalCompare);
        }

        this.updateOptionCards();
      }

      // Recalcula os preços visíveis de cada card a partir do preço da variante
      // atual (o Liquid renderiza com o preço da variante inicial).
      updateOptionCards() {
        this.querySelectorAll(".quantity-bundle__option").forEach((option) => {
          const radio = option.querySelector(".quantity-bundle__radio-input");
          const quantity = parseInt(option.dataset.quantity);
          const position = parseInt(radio?.dataset.position);
          if (!quantity || !position) return;

          const discountDecimal =
            (100 - (this.discounts[position - 1] || 0)) / 100;

          const total = this.basePrice * quantity;
          const totalDiscounted = total * discountDecimal;

          const totalEl = option.querySelector(".quantity-bundle__option-total");
          if (totalEl) {
            const realUnits = quantity * this.unitsPerPack;
            totalEl.textContent = `${this.formatPrice(
              totalDiscounted / realUnits,
            )} / und`;
          }

          const strikeEl = option.querySelector(
            ".quantity-bundle__option-strache",
          );
          if (strikeEl) strikeEl.textContent = this.formatPrice(total);

          const eachEl = option.querySelector(".quantity-bundle__price-each");
          if (eachEl) eachEl.textContent = this.formatPrice(totalDiscounted);

          const hintEl = option.querySelector(".quantity-bundle__fs-hint");
          // So na faixa escolhida: em todas de uma vez vira ruido.
          if (hintEl) this.renderHint(hintEl, quantity, totalDiscounted, !!radio?.checked);

          const saveEl = option.querySelector(".quantity-bundle__tag--save");
          if (saveEl) {
            saveEl.textContent = `Economize ${this.formatPrice(
              total - totalDiscounted,
            )}`;
          }
        });
      }

      // Aviso sob o botao: confirma o frete gratis ou diz quantos packs faltam.
      updateShippingNote(totalCurrent, quantity) {
        if (!this.shippingNoteEl || !this.freeShippingMin) return;

        const totalCents = Math.round(totalCurrent * 100);
        const reached = totalCents >= this.freeShippingMin;

        if (reached) {
          this.shippingNoteEl.textContent = "Você ganhou frete grátis";
        } else {
          const target = this.getFreeShippingTarget();
          if (target && quantity && target.quantity > quantity) {
            const gap = target.quantity - quantity;
            this.shippingNoteEl.textContent =
              gap === 1
                ? "Falta 1 pack para o frete grátis"
                : `Faltam ${gap} packs para o frete grátis`;
          } else {
            // Sem faixa maior para comparar: volta a falar em valor.
            const missing = (this.freeShippingMin - totalCents) / 100;
            this.shippingNoteEl.textContent = `Faltam ${this.formatPrice(
              missing,
            )} para o frete grátis`;
          }
        }

        if (this.shippingBadgeEl) {
          this.shippingBadgeEl.dataset.shippingReached = reached ? "true" : "false";
        }
      }

      // Alvo do frete gratis: a faixa mais barata cujo total ja alcanca o minimo.
      getFreeShippingTarget() {
        if (!this.freeShippingMin) return null;

        let target = null;
        this.quantities.forEach((quantity, index) => {
          if (!quantity) return;
          const discount = this.discounts[index] || 0;
          const total = this.basePrice * quantity * ((100 - discount) / 100);
          if (Math.round(total * 100) < this.freeShippingMin) return;
          if (!target || total < target.total) {
            target = { total: total, quantity: quantity, discount: discount };
          }
        });
        return target;
      }

      renderHint(hintEl, quantity, totalDiscounted, isSelected) {
        const target = this.getFreeShippingTarget();
        const totalCents = Math.round(totalDiscounted * 100);

        if (!isSelected || !target || totalCents >= this.freeShippingMin || quantity >= target.quantity) {
          hintEl.textContent = "";
          hintEl.classList.add("quantity-bundle__fs-hint--hidden");
          return;
        }

        // Distancia em packs: mais tangivel que o valor em reais.
        const gapPacks = target.quantity - quantity;
        const amount = gapPacks === 1 ? "+1 pack" : `+${gapPacks} packs`;

        const discount = Math.round(target.discount);
        hintEl.textContent =
          discount > 0
            ? `${amount}: ${discount}% OFF e frete grátis`
            : `${amount} e frete grátis`;
        hintEl.classList.remove("quantity-bundle__fs-hint--hidden");
      }

      updateVisualSelection() {
        this.querySelectorAll(".quantity-bundle__option").forEach((option) =>
          option.classList.remove("quantity-bundle__option--selected"),
        );

        this.querySelector(".quantity-bundle__radio-input:checked")
          ?.closest(".quantity-bundle__option")
          ?.classList.add("quantity-bundle__option--selected");
      }

      updateButtonState() {
        if (this.isSubmitting) return;

        this.buyButton.disabled = !this.variantAvailable;
        this.buyButton.textContent = this.variantAvailable
          ? this.originalButtonText
          : window.variantStrings?.soldOut || "Esgotado";
      }

      formatPrice(price) {
        return "R$ " + price.toFixed(2).replace(".", ",");
      }

      async addToCart() {
        if (this.isSubmitting || !this.variantAvailable) return;

        try {
          this.isSubmitting = true;
          this.buyButton.disabled = true;
          this.buyButton.textContent = "Adicionando...";

          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: this.variantId,
              quantity: this.selectedQuantity,
            }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(error?.description || "Erro ao adicionar ao carrinho");
          }

          this.showSuccessFeedback();
          setTimeout(() => this.openCartDrawer(), 300);
        } catch (error) {
          console.error(error);
          this.showErrorFeedback();
        }
      }

      showSuccessFeedback() {
        this.buyButton.textContent = "✓ Adicionado!";
        setTimeout(() => this.restoreButton(), 2000);
      }

      showErrorFeedback() {
        this.buyButton.textContent = "Erro - tente novamente";
        setTimeout(() => this.restoreButton(), 2500);
      }

      restoreButton() {
        this.isSubmitting = false;
        this.updateButtonState();
      }

      async openCartDrawer() {
        const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
        const sections = await response.json();

        const drawer = document.querySelector("cart-drawer");
        if (!drawer) return;

        drawer.classList.remove("is-empty");
        drawer.renderContents({ sections });

        requestAnimationFrame(() => {
          drawer.classList.add("animate", "active");
          document.body.classList.add("overflow-hidden");
        });
      }
    },
  );
}
